<script setup lang="ts">
//#region imports
import { computed } from 'vue';
import { usePermissions } from '@/composables/usePermissions';
//#endregion

//#region types
type MenuItem = {
  name: string;
  link?: string;
  permission?: string;
  sections?: MenuItem[];
};

interface Props {
  menu?: MenuItem[],
  currentRoute?: string,
}
//#endregion

//#region define
const props = withDefaults(defineProps<Props>(), {
  menu: () => [],
  currentRoute: () => '',
});
//#endregion

//#region composables
const { hasPermission } = usePermissions();
//#endregion

//#region computed
const menuFiltered = computed(() => {
  return props.menu
    .filter((item) => !item.permission || hasPermission(item.permission))
    .map((item) => {
      if (!item.sections) return item;
      return {
        ...item,
        sections: item.sections.filter((section) => !section.permission || hasPermission(section.permission)),
      };
    })
    .filter((item) => !item.sections || item.sections.length > 0);
});
//#endregion
</script>

<template>
  <div class="sub-header">
    <div class="content-wrapper">
      <div class="t:flex t:items-stretch">
        <slot />
        <div
          v-if="menuFiltered.length > 0"
          class="sub-header__menu"
        >
          <div
            v-for="item in menuFiltered"
            :key="item.name"
            :class="[
              'sub-header__menu-item',
              { 'sub-header__menu-item_active': props.currentRoute === item.name }
            ]"
          >
            <RouterLink
              v-if="item.link"
              :title="item.name"
              :to="item.link"
            >
              {{ item.name }}
            </RouterLink>
            <span v-else>
              {{ item.name }}
            </span>
            <template v-if="item.sections && item.sections.length > 0">
              <UiIcon
                name="chevron-down"
                :size="9"
                color="inherit"
                class="t:ml-4"
              />
              <div
                class="sub-header__menu-dropdown"
              >
                <UiScrollbar
                  wrap-style="max-height: 400px;"
                  :native="false"
                >
                  <div
                    v-for="section in item.sections"
                    :key="section.name"
                    class="sub-header__menu-dropdown-item"
                  >
                    <RouterLink
                      :title="section.name"
                      :to="section.link!"
                    >
                      {{ section.name }}
                    </RouterLink>
                  </div>
                </UiScrollbar>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
.sub-header {
  $root: &;

  background: var(--header-submenu);
  color: var(--header-text);

  &__menu {
    display: flex;
    border-left: 1px solid var(--header-divider);
  }
  &__menu-item {
    position: relative;
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    justify-content: center;
    cursor: pointer;
    padding: 0 8px;
    height: 40px;
    font-size: 14px;
    font-weight: 400;
    text-decoration: none;

    &_active {
      color: var(--header-text-active)
    }

    &_active,
    &:hover {
      box-shadow: inset 0 -2px 0 0 var(--border-accent);
      background-color: var(--overlay-black-20);
    }
    &:hover {
      #{$root}__menu-dropdown {
        display: block;
      }
    }
  }
  &__menu-dropdown {
    display: none;
    position: absolute;
    z-index: 200;
    left: 0;
    box-sizing: border-box;
    min-width: 120px;
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.2);
    top: 100%;
    background: var(--header-submenu);
  }
  &__menu-dropdown-item {
    padding-left: 8px;
    padding-right: 32px;
    height: 34px;
    width: 100%;
    position: relative;
    color: var(--text-body-invert);
    font-size: 12px;
    text-decoration: none;
    white-space: nowrap;
    max-width: 400px;
    text-overflow: ellipsis;
    overflow-x: hidden;
    line-height: 2.4;
    display: flex;
    align-items: center;

    .router-link-active{
      color: var(--header-text-active)
    }

    &:hover {
      background-color: var(--header-menu);
    }
  }
}
</style>
